import * as React from 'react'
import useVehicleService from '../services/VehicleService'
import { VehicleGroupModel } from '@/app/models/vehicle/vehicleGroupModel'
import { VehicleModel } from '@/app/models'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type EditVehicleGroupsContextType = {
  vehiclesInGroup: string[]
  selectedGroup: string[]
  isSelectedAny: boolean
  addedIds: string[]
  removedIds: string[]
  toggleAll: (checked: boolean) => void
  toggleId: (id: string) => void
  confirmEditGroupMembers: () => Promise<void>
  loading: boolean
}

const EditVehicleGroupsContext = React.createContext<
  EditVehicleGroupsContextType | undefined
>(undefined)

export const EditVehicleGroupsProvider = ({
  children,
  companyId,
  group,
  vehicles,
}: {
  children: React.ReactNode
  vehicles: VehicleModel[]
  companyId: string
  group: VehicleGroupModel
}) => {
  const { getVehiclesInGroup, addVehicleToGroup, removeVehicleFromGroup, activateVehicle } =
    useVehicleService()
  const { t } = useLocalize()
  const [loading, setLoading] = React.useState(false)
  const [vehiclesInGroup, setVehiclesInGroup] = React.useState<string[]>([])
  const [selectedGroup, setSelectedGroup] = React.useState<string[]>([])
  const [initialIds, setInitialIds] = React.useState<string[]>([])

  const addedIds = React.useMemo(
    () => selectedGroup.filter((id) => !initialIds.includes(id)),
    [selectedGroup, initialIds],
  )

  const removedIds = React.useMemo(
    () => initialIds.filter((id) => !selectedGroup.includes(id)),
    [selectedGroup, initialIds],
  )

  const fetchVehiclesInGroup = async () => {
    setLoading(true)
    try {
      if (group.id < 0) {
        // Default groups: filter passed vehicles by isActive
        const filtered = vehicles
          .filter((v) => v.isActive === (group.id === -1))
          .map((v) => String(v.id))
        setVehiclesInGroup(filtered)
        setInitialIds(filtered)
        setLoading(false)
        return
      }
      const res = await getVehiclesInGroup({ companyId, groupId: group.id })
      const ids = (res as any[]).map(String) // API returns numbers, normalize to strings
      setVehiclesInGroup(ids)
      setInitialIds(ids)
    } catch {
      toast.error({ text: t('vehicleModule.error-fetching-vehicles-in-group') })
    } finally {
      setLoading(false)
    }
  }

  const toggleId = (id: string) => {
    setSelectedGroup((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id)
      }
      return [...prev, id]
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedGroup(vehiclesInGroup)
    } else {
      setSelectedGroup([])
    }
  }

  const confirmEditGroupMembers = async () => {
    setLoading(true)
    const isAnyAdded = addedIds.length > 0
    const isAnyRemoved = removedIds.length > 0

    if (!isAnyAdded && !isAnyRemoved) {
      toast.info({ text: t('vehicleModule.no-changes-to-save') })
      setLoading(false)
      return
    }

    try {
      if (group.id > 0) {
        if (isAnyRemoved) {
          await removeVehicleFromGroup({ companyId, groupId: group.id, vehicleIds: removedIds })
        }
        if (isAnyAdded) {
          await addVehicleToGroup({ companyId, groupId: group.id, vehicleIds: addedIds })
        }
      } else {
        // Default groups (-1 active, -2 inactive) → activate/deactivate
        if (isAnyAdded) {
          await activateVehicle({ companyId, vehicleIds: addedIds, setActive: group.id === -1 })
        }
        if (isAnyRemoved) {
          await activateVehicle({ companyId, vehicleIds: removedIds, setActive: group.id !== -1 })
        }
      }
      toast.success({ text: t('vehicleModule.group-members-updated') })
    } catch {
      toast.error({ text: t('vehicleModule.error-updating-group-members') })
    } finally {
      setLoading(false)
      await fetchVehiclesInGroup()
    }
  }

  React.useEffect(() => {
    if (companyId && group) {
      fetchVehiclesInGroup()
    }
  }, [companyId, group])

  React.useEffect(() => {
    setSelectedGroup(vehiclesInGroup)
  }, [vehiclesInGroup])

  const value = React.useMemo(
    () => ({
      loading,
      vehiclesInGroup,
      selectedGroup,
      isSelectedAny: selectedGroup.length > 0,
      addedIds,
      removedIds,
      toggleId,
      toggleAll,
      confirmEditGroupMembers,
    }),
    [loading, vehiclesInGroup, selectedGroup, addedIds, removedIds],
  )

  return (
    <EditVehicleGroupsContext.Provider value={value}>
      {children}
    </EditVehicleGroupsContext.Provider>
  )
}

export const useEditVehicleGroups = (): EditVehicleGroupsContextType => {
  const ctx = React.useContext(EditVehicleGroupsContext)
  if (!ctx) {
    throw new Error('useEditVehicleGroups must be used within EditVehicleGroupsProvider')
  }
  return ctx
}
